import React, { useEffect, useRef } from 'react';

const AnimatedBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let time = 0;
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Flowing line class for smooth market-inspired curves
    class FlowingLine {
      constructor(yPosition, amplitude, frequency, speed, color, opacity) {
        this.yPosition = yPosition;
        this.amplitude = amplitude;
        this.frequency = frequency;
        this.speed = speed;
        this.color = color;
        this.opacity = opacity;
        this.offset = Math.random() * Math.PI * 2;
      }

      draw(time) {
        ctx.beginPath();
        ctx.strokeStyle = this.color;
        ctx.globalAlpha = this.opacity;
        ctx.lineWidth = 2;

        for (let x = 0; x < canvas.width; x += 5) {
          const y = this.yPosition + 
            Math.sin((x * this.frequency) + (time * this.speed) + this.offset) * this.amplitude;
          
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }

    // Network dot class for subtle connections
    class NetworkDot {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.baseX = this.x;
        this.baseY = this.y;
        this.size = Math.random() * 1.5 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.15;
        this.speedY = (Math.random() - 0.5) * 0.15;
        this.opacity = Math.random() * 0.15 + 0.05;
        this.pulseSpeed = Math.random() * 0.002 + 0.001;
        this.pulsePhase = Math.random() * Math.PI * 2;
      }

      update(time) {
        // Gentle floating motion
        this.x += this.speedX;
        this.y += this.speedY;

        // Boundary wrapping
        if (this.x < 0) this.x = canvas.width;
        if (this.x > canvas.width) this.x = 0;
        if (this.y < 0) this.y = canvas.height;
        if (this.y > canvas.height) this.y = 0;

        // Subtle pulsing
        this.currentOpacity = this.opacity + Math.sin(time * this.pulseSpeed + this.pulsePhase) * 0.03;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(14, 165, 164, ${this.currentOpacity})`;
        ctx.fill();
      }
    }

    // Initialize flowing lines with soft teal colors
    const flowingLines = [
      new FlowingLine(canvas.height * 0.2, 40, 0.003, 0.0008, 'rgba(14, 165, 164, 1)', 0.08),
      new FlowingLine(canvas.height * 0.4, 60, 0.002, 0.0006, 'rgba(31, 162, 166, 1)', 0.06),
      new FlowingLine(canvas.height * 0.6, 50, 0.0025, 0.0007, 'rgba(43, 176, 166, 1)', 0.07),
      new FlowingLine(canvas.height * 0.8, 45, 0.0028, 0.0009, 'rgba(14, 165, 164, 1)', 0.05),
    ];

    // Initialize network dots
    const networkDots = [];
    const dotCount = Math.min(Math.floor((canvas.width * canvas.height) / 25000), 50);
    for (let i = 0; i < dotCount; i++) {
      networkDots.push(new NetworkDot());
    }

    // Draw soft grid
    const drawGrid = () => {
      ctx.strokeStyle = 'rgba(14, 165, 164, 0.02)';
      ctx.lineWidth = 1;
      
      const gridSize = 80;
      
      // Vertical lines
      for (let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      
      // Horizontal lines
      for (let y = 0; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
    };

    // Draw soft gradient overlay
    const drawGradient = () => {
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 3, 0,
        canvas.width / 2, canvas.height / 3, canvas.height
      );
      gradient.addColorStop(0, 'rgba(14, 165, 164, 0.03)');
      gradient.addColorStop(0.5, 'rgba(14, 165, 164, 0.01)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    // Animation loop
    const animate = () => {
      // Clear canvas with dark navy/charcoal background
      ctx.fillStyle = '#0a0e14';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw subtle grid
      drawGrid();

      // Draw gradient overlay
      drawGradient();

      // Draw flowing lines
      flowingLines.forEach(line => line.draw(time));

      // Update and draw network dots
      networkDots.forEach((dot, index) => {
        dot.update(time);
        dot.draw();

        // Draw subtle connections between nearby dots
        for (let j = index + 1; j < networkDots.length; j++) {
          const dx = networkDots[j].x - dot.x;
          const dy = networkDots[j].y - dot.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(14, 165, 164, ${0.03 * (1 - distance / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(dot.x, dot.y);
            ctx.lineTo(networkDots[j].x, networkDots[j].y);
            ctx.stroke();
          }
        }
      });

      time += 0.5; // Very slow time increment for calm motion
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="animated-background-canvas"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    />
  );
};

export default AnimatedBackground;
